import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarOff, Pencil, Plus } from 'lucide-react';

import type { Justica, LugarFiltro, RegraForense } from '@/api/calendarioForense';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotification } from '@/context/NotificationProvider';
import { useDebounce } from '@/hooks/useDebounce';
import { useDefinirRegraAtiva, useRegrasForenses } from '@/hooks/useCalendarioForense';
import { classifyError, notifyMutationError } from '@/lib/errors';
import {
  JUSTICAS,
  UFS,
  descreverJusticas,
  descreverOnde,
  descreverQuando,
  filtrarPorTexto,
  fmtData,
  rotuloTipo,
} from './calendarioForense';
import { ConferirCalendario } from './ConferirCalendario';
import { RegraForenseDialog } from './RegraForenseDialog';
import { SugestoesForenses } from './SugestoesForenses';

const TODAS = '__todas__';

interface FiltroLugar {
  justica: Justica | '';
  tribunal: string;
  uf: string;
  ibge: string;
  segundoGrau: boolean;
}

const FILTRO_VAZIO: FiltroLugar = {
  justica: '',
  tribunal: '',
  uf: '',
  ibge: '',
  segundoGrau: false,
};

function paraLugar(f: FiltroLugar): LugarFiltro {
  return {
    justica: f.justica || undefined,
    tribunal: f.tribunal.trim() || undefined,
    uf: f.uf || undefined,
    municipio_ibge: f.ibge.length === 7 ? Number(f.ibge) : undefined,
    segundo_grau: f.segundoGrau || undefined,
  };
}

function FiltroDoLugar({ filtro, onChange }: { filtro: FiltroLugar; onChange: (f: FiltroLugar) => void }) {
  const set = (parcial: Partial<FiltroLugar>) => onChange({ ...filtro, ...parcial });
  const vazio = JSON.stringify(filtro) === JSON.stringify(FILTRO_VAZIO);
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <p className="text-sm text-muted-foreground">
          O lugar do ato: as regras e a conferência mostram o que vale nele. Vazio, a lista traz todas as regras.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="lugar-justica">Justiça</Label>
            <Select
              value={filtro.justica || TODAS}
              onValueChange={(v) => set({ justica: v === TODAS ? '' : (v as Justica) })}
            >
              <SelectTrigger id="lugar-justica" className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Não informada</SelectItem>
                {JUSTICAS.map((j) => (
                  <SelectItem key={j.value} value={j.value}>
                    {j.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-28 space-y-1">
            <Label htmlFor="lugar-tribunal">Tribunal</Label>
            <Input
              id="lugar-tribunal"
              placeholder="TJAL"
              maxLength={16}
              value={filtro.tribunal}
              onChange={(e) =>
                set({
                  tribunal: e.target.value.toUpperCase().replace(/\s/g, ''),
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="lugar-uf">UF</Label>
            <Select value={filtro.uf || TODAS} onValueChange={(v) => set({ uf: v === TODAS ? '' : v })}>
              <SelectTrigger id="lugar-uf" className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>—</SelectItem>
                {UFS.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32 space-y-1">
            <Label htmlFor="lugar-ibge">Município (IBGE)</Label>
            <Input
              id="lugar-ibge"
              inputMode="numeric"
              maxLength={7}
              placeholder="2704302"
              value={filtro.ibge}
              onChange={(e) => set({ ibge: e.target.value.replace(/\D/g, '') })}
            />
          </div>
          <div className="flex min-h-11 items-center gap-2">
            <Switch id="lugar-2grau" checked={filtro.segundoGrau} onCheckedChange={(v) => set({ segundoGrau: v })} />
            <Label htmlFor="lugar-2grau">Ato no 2º grau</Label>
          </div>
          {!vazio && (
            <Button variant="ghost" className="min-h-11" onClick={() => onChange(FILTRO_VAZIO)}>
              Limpar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ListaDeRegras({ lugar, onEditar }: { lugar: LugarFiltro; onEditar: (r: RegraForense) => void }) {
  const { showSuccess, showError } = useNotification();
  const [busca, setBusca] = useState('');
  const [incluirInativas, setIncluirInativas] = useState(false);
  const { data, isLoading, error } = useRegrasForenses(lugar, incluirInativas);
  const definirAtivo = useDefinirRegraAtiva();
  const regras = useMemo(() => filtrarPorTexto(data ?? [], busca), [data, busca]);

  const alternar = (r: RegraForense, ativo: boolean) =>
    definirAtivo.mutate(
      { id: r.id, ativo },
      {
        onSuccess: () =>
          showSuccess(
            ativo
              ? 'Regra ligada. Os vencimentos afetados serão refeitos.'
              : 'Regra desligada. Os vencimentos afetados serão refeitos.',
          ),
        onError: (err) => notifyMutationError(err, showError),
      },
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por nome, ato ou lugar"
          className="sm:max-w-sm"
        />
        <div className="flex min-h-11 items-center gap-2">
          <Switch id="regras-inativas" checked={incluirInativas} onCheckedChange={setIncluirInativas} />
          <Label htmlFor="regras-inativas">Mostrar desligadas</Label>
        </div>
      </div>

      {isLoading && <Spinner size={32} label="Carregando regras" />}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}
      {!isLoading && !error && regras.length === 0 && (
        <EmptyState
          icon={<CalendarOff />}
          title="Nenhuma regra"
          description={busca ? 'Nenhuma regra com essa busca.' : 'Nenhuma regra vale neste lugar.'}
        />
      )}

      {regras.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Regra</TableHead>
                  <TableHead>Quando</TableHead>
                  <TableHead className="hidden sm:table-cell">Onde</TableHead>
                  <TableHead className="hidden md:table-cell">Próxima</TableHead>
                  <TableHead>Ligada</TableHead>
                  <TableHead className="w-12">
                    <span className="sr-only">Editar</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regras.map((r) => {
                  const justicas = descreverJusticas(r.justicas);
                  return (
                    <TableRow key={r.id} className={r.ativo ? undefined : 'opacity-60'}>
                      <TableCell className="min-w-[11rem]">
                        <div className="font-medium">{r.nome}</div>
                        <div className="text-xs text-muted-foreground">{r.ato_normativo ?? 'Sem ato'}</div>
                        <div className="mt-1 text-xs sm:hidden">{descreverOnde(r)}</div>
                      </TableCell>
                      <TableCell className="min-w-[9rem]">
                        <div>{descreverQuando(r)}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <Badge variant={r.tipo === 'feriado' ? 'secondary' : 'outline'}>{rotuloTipo(r.tipo)}</Badge>
                          {r.excecoes && Object.keys(r.excecoes).length > 0 && (
                            <Badge variant="outline">
                              {Object.keys(r.excecoes).length === 1
                                ? '1 exceção'
                                : `${Object.keys(r.excecoes).length} exceções`}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden min-w-[9rem] sm:table-cell">
                        <div>{descreverOnde(r)}</div>
                        {justicas && <div className="text-xs text-muted-foreground">Só: {justicas}</div>}
                      </TableCell>
                      <TableCell className="hidden tabular-nums md:table-cell">
                        {r.proximas.length ? fmtData(r.proximas[0]) : '—'}
                      </TableCell>
                      <TableCell>
                        {/* O label amplia a área de toque do interruptor para 44px. */}
                        <label className="flex min-h-11 min-w-11 cursor-pointer items-center">
                          <Switch
                            checked={r.ativo}
                            disabled={definirAtivo.isPending}
                            onCheckedChange={(v) => alternar(r, v)}
                            aria-label={`${r.ativo ? 'Desligar' : 'Ligar'} ${r.nome}`}
                          />
                        </label>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11"
                          onClick={() => onEditar(r)}
                          aria-label={`Editar ${r.nome}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const ABAS = ['regras', 'conferir', 'sugestoes'];

export default function CalendarioForensePage() {
  // O e-mail de sugestão aos admins abre direto na fila (?aba=sugestoes).
  const [params] = useSearchParams();
  const abaInicial = ABAS.includes(params.get('aba') ?? '') ? (params.get('aba') as string) : 'regras';
  const [filtro, setFiltro] = useState<FiltroLugar>(FILTRO_VAZIO);
  const filtroEstavel = useDebounce(filtro, 300);
  const lugar = useMemo(() => paraLugar(filtroEstavel), [filtroEstavel]);
  // `undefined` = fechado; `null` = nova regra.
  const [editando, setEditando] = useState<RegraForense | null | undefined>(undefined);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Calendário forense"
        subtitle="Feriados, recessos e suspensões de prazo que entram na conta dos vencimentos de todos os usuários. Cada regra cita o ato que a declara."
        icon={<CalendarOff />}
        actions={
          <Button className="min-h-11" onClick={() => setEditando(null)}>
            <Plus className="mr-1 h-4 w-4" />
            Nova regra
          </Button>
        }
      />

      <FiltroDoLugar filtro={filtro} onChange={setFiltro} />

      <Tabs defaultValue={abaInicial}>
        <TabsList>
          <TabsTrigger value="regras">Regras</TabsTrigger>
          <TabsTrigger value="conferir">Conferir</TabsTrigger>
          <TabsTrigger value="sugestoes">Sugestões</TabsTrigger>
        </TabsList>
        <TabsContent value="regras" className="mt-4">
          <ListaDeRegras lugar={lugar} onEditar={setEditando} />
        </TabsContent>
        <TabsContent value="conferir" className="mt-4">
          <ConferirCalendario lugar={lugar} />
        </TabsContent>
        <TabsContent value="sugestoes" className="mt-4">
          <SugestoesForenses />
        </TabsContent>
      </Tabs>

      {editando !== undefined && (
        <RegraForenseDialog key={editando?.id ?? 'nova'} regra={editando} onClose={() => setEditando(undefined)} />
      )}
    </div>
  );
}
